/**
 * MapSelectionDialog
 *
 * Lets you move/zoom the map and pick the area to show
 * on the kiosk screen. The green box is exactly what will be shown.
 *
 * summary:
 * - pigeon-maps (no Leaflet) with station markers and zoom controls
 * - Emits `{ center, zoom, bounds }` for the center 80% of the map view (10% inset)
 * - Optional language toggles for map labels
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Handler for dialog close
 * @param {Function} props.onMapAreaSelect - Handler for map area selection
 * @param {Object} props.currentMapConfig - Current map configuration
 * @param {Object} props.mapLanguages - Current map language configuration
 * @param {Function} props.onMapLanguageChange - Handler for map language changes
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Alert,
  Checkbox,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MapIcon from "@mui/icons-material/Map";
import AirIcon from "@mui/icons-material/Air";
import { Map, Marker, ZoomControl } from "pigeon-maps";
import {
  kioskMapTileAttributionHtml,
  kioskMapTileProvider,
  useKioskPigeonMapSize,
} from "./utils/kioskPigeonMapUtils";

// Local imports
import iconImage from "../Map/weather-station(color).png";
import { useWeatherStationsMap } from "../../services/useWeatherStationsMap";
import { useTranslation } from "../../context/TranslationContext";
import { getStationMarkerHtml } from "./components/MapIconFactory";

// Constants
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  MAP_MIN_ZOOM,
  MAP_MAX_ZOOM,
  PIGEON_MAP_LIMIT_BOUNDS_FULL_VIEWPORT,
} from "./utils/constants";

/**
 * MapSelectionDialog Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Handler for dialog close
 * @param {Function} props.onMapAreaSelect - Handler for map area selection
 * @param {Object} props.currentMapConfig - Current map configuration
 * @returns {JSX.Element} The map selection dialog
 */
const MapSelectionDialog = ({
  open,
  onClose,
  onMapAreaSelect,
  currentMapConfig,
  mapLanguages = { english: true, inuktitut: true },
  onMapLanguageChange,
  showWindBarbMap = false,
  onWindBarbMapToggle,
  windBarbMapLanguages = { english: true, inuktitut: true },
  onWindBarbMapLanguageChange,
}) => {
  useTranslation();

  // Weather stations data - only fetch when dialog is open
  const {
    data: weatherStations,
    isLoading,
    error,
  } = useWeatherStationsMap({
    enabled: open, // Only enable the query when dialog is open
  });

  // Map configuration state
  const [selectedMapArea, setSelectedMapArea] = useState(
    currentMapConfig || {
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
      bounds: null,
    }
  );

  // Current map view state for real-time preview
  const [currentMapView, setCurrentMapView] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const pigeonMapRef = useRef(null);
  const [mapWrapperRef, mapSize] = useKioskPigeonMapSize();

  // Language selection handler
  const handleLanguageChange = (language) => (event) => {
    if (!onMapLanguageChange) return;

    const checked = event.target.checked;

    // If trying to uncheck a language, ensure at least one language remains selected
    if (!checked) {
      const otherLanguage = language === "english" ? "inuktitut" : "english";
      if (!mapLanguages[otherLanguage]) {
        // Don't allow deselecting if it would leave no languages selected
        return;
      }
    }

    onMapLanguageChange({
      ...mapLanguages,
      [language]: checked,
    });
  };

  // Reset selected map area when dialog opens with new config
  useEffect(() => {
    if (open && currentMapConfig) {
      setSelectedMapArea(currentMapConfig);
      setCurrentMapView(currentMapConfig);
    } else if (open && !currentMapConfig) {
      const defaultConfig = {
        center: DEFAULT_MAP_CENTER,
        zoom: DEFAULT_MAP_ZOOM,
        bounds: null,
      };
      setSelectedMapArea(defaultConfig);
      setCurrentMapView(defaultConfig);
    }
  }, [open, currentMapConfig]);

  /**
   * Handles real-time map view changes
   */
  const handleMapChange = (mapView) => {
    setCurrentMapView(mapView);
  };

  /**
   * Handles map area selection from current map view
   */
  const handleMapAreaSelect = () => {
    if (pigeonMapRef.current && currentMapView && mapWrapperRef.current) {
      setIsSelecting(true);
      setTimeout(() => {
        try {
          const map = pigeonMapRef.current;
          const container = mapWrapperRef.current;
          const width = container.offsetWidth;
          const height = container.offsetHeight;
          const left = width * 0.1;
          const right = width * 0.9;
          const top = height * 0.1;
          const bottom = height * 0.9;
          const southWest = map.pixelToLatLng([left, bottom]);
          const northEast = map.pixelToLatLng([right, top]);
          const greenBoxBounds = [
            [southWest[0], southWest[1]],
            [northEast[0], northEast[1]],
          ];
          const newMapView = {
            ...currentMapView,
            bounds: greenBoxBounds,
          };
          setSelectedMapArea(newMapView);
          onMapAreaSelect(newMapView);
        } catch (error) {
          console.warn("Map not ready for selection:", error);
        } finally {
          setIsSelecting(false);
        }
      }, 500);
    }
  };

  /**
   * Handles dialog close
   */
  const handleClose = () => {
    onClose();
  };

  /**
   * Formats coordinates for display
   */
  const formatCoordinates = (lat, lng) => {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  };

  const mapViewForRender = currentMapView ?? selectedMapArea;
  const pigeonAttribution = useMemo(
    () => (
      <span dangerouslySetInnerHTML={{ __html: kioskMapTileAttributionHtml }} />
    ),
    []
  );

  // Error state
  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Map Selection</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error">Error loading map: {error.message}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Map Selection</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
          >
            <Typography variant="body1">Loading weather stations...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Dialog Header */}
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Select Map Area for Kiosk Display
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent>
        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Navigate and zoom the map, then use Select Current View to save the
            kiosk area.
          </Typography>
        </Alert>

        {/* 1. Station Map with Temperature */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor:
              mapLanguages?.english || mapLanguages?.inuktitut
                ? "primary.main"
                : "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <MapIcon
                color={
                  mapLanguages?.english || mapLanguages?.inuktitut
                    ? "primary"
                    : "action"
                }
                sx={{ fontSize: 20 }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Station Map with Temperature Data
              </Typography>
              <Chip
                label="5s"
                size="small"
                color="primary"
                variant="outlined"
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {mapLanguages?.english || mapLanguages?.inuktitut
                  ? "Enabled"
                  : "Disabled"}
              </Typography>
              <Switch
                checked={mapLanguages?.english || mapLanguages?.inuktitut}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Enable both languages by default when turning on
                    onMapLanguageChange &&
                      onMapLanguageChange({ english: true, inuktitut: true });
                  } else {
                    // Disable both languages
                    onMapLanguageChange &&
                      onMapLanguageChange({ english: false, inuktitut: false });
                  }
                }}
                color="primary"
              />
            </Stack>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: mapLanguages?.english || mapLanguages?.inuktitut ? 2 : 0,
            }}
          >
            Shows weather stations on the map with their temperature data.
          </Typography>

          {(mapLanguages?.english || mapLanguages?.inuktitut) && (
            <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, fontWeight: 600 }}
              >
                Select Languages (at least one required):
              </Typography>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mapLanguages?.english || false}
                      onChange={handleLanguageChange("english")}
                      disabled={
                        !mapLanguages?.inuktitut && mapLanguages?.english
                      }
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">English</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mapLanguages?.inuktitut || false}
                      onChange={handleLanguageChange("inuktitut")}
                      disabled={
                        !mapLanguages?.english && mapLanguages?.inuktitut
                      }
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Inuktitut</Typography>}
                />
              </Stack>
            </Box>
          )}
        </Paper>

        {/* 2. Wind Barb Map */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: showWindBarbMap ? "success.main" : "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AirIcon
                color={showWindBarbMap ? "success" : "action"}
                sx={{ fontSize: 20 }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Station Map with Meteorological Wind Barbs
              </Typography>
              <Chip
                label="5s"
                size="small"
                color="success"
                variant="outlined"
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {showWindBarbMap ? "Enabled" : "Disabled"}
              </Typography>
              <Switch
                checked={showWindBarbMap}
                onChange={(e) =>
                  onWindBarbMapToggle && onWindBarbMapToggle(e.target.checked)
                }
                color="success"
              />
            </Stack>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: showWindBarbMap ? 2 : 0 }}
          >
            Displays wind speed and direction using meteorological wind barbs.
            Uses the same map area selected below.
          </Typography>

          {showWindBarbMap && (
            <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, fontWeight: 600 }}
              >
                Select Languages (at least one required):
              </Typography>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={windBarbMapLanguages?.english || false}
                      onChange={(e) => {
                        if (!onWindBarbMapLanguageChange) return;
                        if (
                          !e.target.checked &&
                          !windBarbMapLanguages?.inuktitut
                        )
                          return;
                        onWindBarbMapLanguageChange({
                          ...windBarbMapLanguages,
                          english: e.target.checked,
                        });
                      }}
                      disabled={
                        !windBarbMapLanguages?.inuktitut &&
                        windBarbMapLanguages?.english
                      }
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">English</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={windBarbMapLanguages?.inuktitut || false}
                      onChange={(e) => {
                        if (!onWindBarbMapLanguageChange) return;
                        if (!e.target.checked && !windBarbMapLanguages?.english)
                          return;
                        onWindBarbMapLanguageChange({
                          ...windBarbMapLanguages,
                          inuktitut: e.target.checked,
                        });
                      }}
                      disabled={
                        !windBarbMapLanguages?.english &&
                        windBarbMapLanguages?.inuktitut
                      }
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Inuktitut</Typography>}
                />
              </Stack>
            </Box>
          )}
        </Paper>

        <Box
          component="span"
          sx={{
            display: "inline-block",
            mb: 1,
            backgroundColor: "#2e7d32",
            color: "white",
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          Kiosk Display Area
        </Box>
        {/* Map Container — green border marks the area you configure */}
        <Paper
          elevation={3}
          sx={{
            height: "60vh",
            width: "100%",
            overflow: "hidden",
            borderRadius: 2,
            border: "3px solid",
            borderColor: isSelecting ? "primary.main" : "success.main",
            transition: "border-color 0.3s ease",
          }}
        >
          <Box
            ref={mapWrapperRef}
            sx={{ height: "100%", width: "100%", position: "relative" }}
          >
            <Map
              ref={pigeonMapRef}
              width={mapSize.width}
              height={mapSize.height}
              center={mapViewForRender.center}
              zoom={mapViewForRender.zoom}
              provider={kioskMapTileProvider}
              attribution={pigeonAttribution}
              attributionPrefix={false}
              minZoom={MAP_MIN_ZOOM}
              maxZoom={MAP_MAX_ZOOM}
              limitBounds={PIGEON_MAP_LIMIT_BOUNDS_FULL_VIEWPORT}
              boxClassname="kiosk-pigeon-map-tiles"
              onBoundsChanged={({ center, zoom, bounds }) => {
                handleMapChange({
                  center,
                  zoom,
                  bounds: [
                    [bounds.sw[0], bounds.sw[1]],
                    [bounds.ne[0], bounds.ne[1]],
                  ],
                });
              }}
            >
              {weatherStations &&
                weatherStations.map((station) => {
                  const {
                    html,
                    width: mw,
                    height: mh,
                  } = getStationMarkerHtml(
                    iconImage,
                    station.name,
                    station.info,
                    station.hasError,
                    station.status,
                    station.type
                  );
                  return (
                    <Marker
                      key={station.id}
                      anchor={[station.lat, station.lon]}
                      width={mw}
                      height={mh}
                    >
                      <div
                        style={{ pointerEvents: "auto" }}
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                    </Marker>
                  );
                })}
              <ZoomControl
                style={{ top: "auto", left: "auto", bottom: 16, right: 16 }}
              />
            </Map>
          </Box>
        </Paper>

        {currentMapView && (
          <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Chip
              label={`Center: ${formatCoordinates(
                currentMapView.center[0],
                currentMapView.center[1]
              )}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Zoom: Level ${currentMapView.zoom}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleMapAreaSelect}
          variant="contained"
          color="primary"
          disabled={isSelecting}
          startIcon={isSelecting ? null : <MyLocationIcon />}
        >
          {isSelecting ? "Selecting..." : "Select Current View"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapSelectionDialog;
